// "Lio questions assigned to me" — the other end of the Superadmin console's
// queue.
//
// Read-only on purpose. The superadmin owns the status, the reply to the
// reporter and the assignment; this page answers the one question the
// assignee has, which is "what did Lio fail at, and what exactly was asked".
// Giving the engineer a Resolve button too would mean two people moving the
// same row with no notion of who won.
import { useEffect, useState } from "react";
import {
  AlertCircle,
  Building2,
  Inbox,
  Loader2,
  MessageSquare,
  Users,
} from "lucide-react";

import {
  getMyAssignedLioIssues,
  type LioIssueStatus,
  type MyLioIssue,
} from "../api/leoIssues";

const STATUS_LABEL: Record<LioIssueStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  dismissed: "Dismissed",
};

const STATUS_CHIP: Record<LioIssueStatus, string> = {
  open: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  dismissed: "bg-gray-100 text-gray-500 border-gray-200",
};

function whenLabel(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function MyLioIssues() {
  const [issues, setIssues] = useState<MyLioIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getMyAssignedLioIssues()
      .then((rows) => alive && setIssues(rows))
      .catch(() => alive && setError("Couldn't load your Lio questions. Please try again."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const open = issues.filter((i) => i.status === "open" || i.status === "in_progress");

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <header className="mb-5">
        <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Inbox className="w-5 h-5 text-emerald-600" />
          Lio questions assigned to me
        </h1>
        <p className="text-xs text-gray-500 mt-1">
          Questions Lio couldn't answer that someone asked you to look at.
          {open.length > 0 && ` ${open.length} still open.`}
        </p>
      </header>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : issues.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-20 text-center text-sm text-gray-500">
          <MessageSquare className="w-6 h-6 text-gray-300" />
          Nothing assigned to you.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {issues.map((issue) => (
            <IssueCard key={issue.id} issue={issue} />
          ))}
        </div>
      )}
    </div>
  );
}

function IssueCard({ issue }: { issue: MyLioIssue }) {
  const [showReply, setShowReply] = useState(false);
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span
          className={`px-2 py-0.5 rounded-full border font-medium ${STATUS_CHIP[issue.status]}`}
        >
          {STATUS_LABEL[issue.status]}
        </span>
        {issue.org_name && (
          <span className="inline-flex items-center gap-1 text-gray-600 font-medium">
            <Building2 className="w-3 h-3" />
            {issue.org_name}
          </span>
        )}
        <span className="text-gray-400">
          asked by {issue.reporter_name}
          {issue.created_at ? ` · ${whenLabel(issue.created_at)}` : ""}
        </span>
        {issue.assignees.length > 1 && (
          // Worth saying who else is on it — a Lio gap is often a backend
          // change AND a prompt change, and two people silently working the
          // same question is the thing to avoid.
          <span className="inline-flex items-center gap-1 text-gray-500">
            <Users className="w-3 h-3" />
            with {issue.assignees.length - 1} other
            {issue.assignees.length - 1 === 1 ? "" : "s"}
          </span>
        )}
        {issue.tool_used && (
          <span className="text-gray-400">· Lio tried {issue.tool_used}</span>
        )}
      </div>

      <p className="text-sm text-gray-900">{issue.question}</p>

      {issue.note && (
        <p className="text-xs text-gray-600">
          <span className="font-semibold">They added:</span> {issue.note}
        </p>
      )}

      {issue.response && (
        <p className="text-xs text-emerald-700">
          <span className="font-semibold">Reply sent to them:</span> {issue.response}
        </p>
      )}

      {/* What Lio actually said. Collapsed: it is often a full Markdown table
          and would bury everything above it. */}
      {issue.reply && (
        <div className="pt-1 border-t border-gray-100">
          <button
            type="button"
            onClick={() => setShowReply((v) => !v)}
            aria-expanded={showReply}
            className="text-[11px] text-gray-400 hover:text-gray-600"
          >
            {showReply ? "Hide what Lio said" : "See what Lio said"}
          </button>
          {showReply && (
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500 font-sans">
              {issue.reply}
            </pre>
          )}
        </div>
      )}

      {(issue.status === "resolved" || issue.status === "dismissed") && (
        <p className="inline-flex items-center gap-1 text-[11px] text-gray-400">
          <AlertCircle className="w-3 h-3" />
          Closed by {issue.reviewed_by_name || "the Lio team"}
          {issue.reviewed_at ? ` · ${whenLabel(issue.reviewed_at)}` : ""}
        </p>
      )}
    </div>
  );
}
